import React, { type CSSProperties } from 'react';

/**
 * Inline subscripts -- the "d" in an engineering variable like Md -- in every
 * text field of a document, on both surfaces.
 *
 * Two storage forms, one per kind of field:
 *
 * - A paragraph stores HTML already, so a subscript there is a `<sub>`
 *   element, written by the browser's own `execCommand('subscript')`.
 * - Every other text field (a heading, a list item, a table cell, a caption)
 *   stores PLAIN text through LiveText, so a subscript there is marked up as
 *   `M_{d}`. LiveText writes the markup into the DOM as a real `<sub>` and
 *   reads it back, so the field shows the subscript while it is edited, not
 *   the braces. Anything outside the document that shows the raw value (a
 *   plan marker label, a search) sees `M_{d}`, which still reads correctly;
 *   `stripSubscripts` gives the bare text where that matters.
 *
 * The look is owned here so the two surfaces cannot drift: a size ratio and a
 * drop, read by the editor's CSS and the PDF's styles alike.
 */

/** Subscript size, as a fraction of the text it sits in. */
export const SUBSCRIPT_SIZE_RATIO = 0.7;

/**
 * How far react-pdf drops a `verticalAlign: 'sub'` run, as a fraction of the
 * RUN'S OWN size. textkit sets a yOffset of -0.2 and multiplies it by the
 * font's units-per-em (2048 for Tinos), and the renderer scales glyph offsets
 * by `fontSize / 1000` -- so the drop is 0.2 * 2048 / 1000 = 0.4096 em. The
 * same un-normalised arithmetic makes `'super'` unusable (see dateOrdinal.ts);
 * for a subscript it happens to land at a normal depth, so the PDF uses it
 * and the editor is told the same number.
 *
 * That makes the drop a fixed multiple of the subscript's size: change the
 * size ratio and the depth follows on both surfaces.
 */
const PDF_SUB_DROP_EM = (0.2 * 2048) / 1000;

/**
 * The editor's half. `em` in vertical-align resolves against the element's
 * OWN font size, so the drop is the PDF's per-run figure as is. Applied at the
 * editor root (DocumentEditor) beside the date-ordinal variables.
 */
export const SUBSCRIPT_CSS_VARS = {
  '--subscript-size': `${SUBSCRIPT_SIZE_RATIO}em`,
  '--subscript-drop': `${-PDF_SUB_DROP_EM}em`,
} as unknown as CSSProperties;

// =======================================
// PLAIN-TEXT MARKUP: M_{d}
// =======================================

export interface InlineSegment {
  text: string;
  sub: boolean;
}

const MARKUP_RE = /_\{([^{}]*)\}/g;

/** Split plain-text markup into runs. A value with no markup is one run. */
export const parseSubscripts = (value: string): InlineSegment[] => {
  const segments: InlineSegment[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  MARKUP_RE.lastIndex = 0;
  while ((match = MARKUP_RE.exec(value)) !== null) {
    if (match.index > last) segments.push({ text: value.slice(last, match.index), sub: false });
    if (match[1]) segments.push({ text: match[1], sub: true });
    last = match.index + match[0].length;
  }
  if (last < value.length || segments.length === 0) segments.push({ text: value.slice(last), sub: false });
  return segments;
};

export const hasSubscripts = (value: string): boolean => {
  MARKUP_RE.lastIndex = 0;
  return MARKUP_RE.test(value);
};

/** The text as read aloud: "M_{d}" -> "Md". */
export const stripSubscripts = (value: string): string =>
  value ? value.replace(MARKUP_RE, '$1') : value;

/**
 * Serialise segments back to markup. Braces cannot be stored inside a
 * subscript (they would end it), so they are dropped from one; adjacent
 * subscript runs merge, so `_{a}_{b}` is never written.
 */
export const formatSubscripts = (segments: InlineSegment[]): string => {
  let out = '';
  let open = false;
  for (const { text, sub } of segments) {
    if (!text) continue;
    if (sub) {
      if (!open) out += '_{';
      out += text.replace(/[{}]/g, '');
      open = true;
    } else {
      if (open) out += '}';
      out += text;
      open = false;
    }
  }
  if (open) out += '}';
  return out;
};

// =======================================
// DOM <-> MARKUP (LiveText)
// =======================================

/**
 * DOM -> markup. Text inside a `<sub>` (however Chrome nested it) becomes
 * `_{...}`; every other element is flattened to its text, exactly as the
 * textContent this replaces did. Reading a DOM written by `writeSubscripts`
 * returns the value it was written from -- LiveText's round-trip rule.
 */
export const readSubscripts = (el: HTMLElement): string => {
  const segments: InlineSegment[] = [];
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = node.textContent || '';
    if (!text) continue;
    const subEl = node.parentElement?.closest('sub');
    segments.push({ text, sub: !!subEl && subEl !== el && el.contains(subEl) });
  }
  return formatSubscripts(segments);
};

/** Markup -> DOM: text nodes, and a `<sub>` for each subscript run. */
export const writeSubscripts = (el: HTMLElement, value: string): void => {
  if (!hasSubscripts(value)) {
    el.textContent = value;
    return;
  }
  const doc = el.ownerDocument;
  const fragment = doc.createDocumentFragment();
  for (const { text, sub } of parseSubscripts(value)) {
    if (!sub) {
      fragment.appendChild(doc.createTextNode(text));
      continue;
    }
    const subEl = doc.createElement('sub');
    subEl.textContent = text;
    fragment.appendChild(subEl);
  }
  el.replaceChildren(fragment);
};

/**
 * Between the two storage forms, for turning one kind of block into another:
 * a paragraph's `<sub>` becomes `_{...}` in a heading or list item, and back.
 */
export const htmlToSubscriptText = (html: string): string => {
  const scratch = document.createElement('div');
  scratch.innerHTML = html;
  return readSubscripts(scratch);
};

export const subscriptTextToHtml = (value: string): string =>
  value ? value.replace(MARKUP_RE, (_, text: string) => (text ? `<sub>${text}</sub>` : '')) : value;

/** Markup -> React children, for a field that is not being edited. */
export const renderSubscripts = (value: string): React.ReactNode => {
  if (!value || !hasSubscripts(value)) return value;
  return parseSubscripts(value).map((segment, i) =>
    segment.sub
      ? React.createElement('sub', { key: i }, segment.text)
      : React.createElement(React.Fragment, { key: i }, segment.text)
  );
};

// =======================================
// INPUT
// =======================================

/** ⌘, (Ctrl+, off a Mac) -- the Google Docs shortcut. */
export const isSubscriptShortcut = (e: KeyboardEvent | React.KeyboardEvent): boolean =>
  e.key === ',' && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey;

// The editable field a node is in, and the <sub> (inside that field) it is in.
const editableOf = (node: Node | null): HTMLElement | null => {
  const el = node && (node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement);
  return (el?.closest('[contenteditable="true"]') as HTMLElement | null) ?? null;
};

const subOf = (node: Node | null, editable: HTMLElement): HTMLElement | null => {
  const el = node && (node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement);
  const sub = el?.closest('sub') as HTMLElement | null;
  return sub && editable.contains(sub) && sub !== editable ? sub : null;
};

// Replace each <sub> in a fragment with its contents.
const unwrapSubs = (fragment: DocumentFragment): void => {
  fragment.querySelectorAll('sub').forEach(sub => sub.replaceWith(...Array.from(sub.childNodes)));
};

/** Whether every character the selection covers is subscripted. */
export const isSelectionSubscript = (): boolean => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  const editable = editableOf(range.commonAncestorContainer);
  if (!editable) return false;
  if (pending && pending.editable === editable && caretIsAt(pending)) return pending.on;
  if (range.collapsed) return !!subOf(range.startContainer, editable);
  const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT);
  let node: Node | null = range.commonAncestorContainer.nodeType === Node.TEXT_NODE ? range.commonAncestorContainer : walker.nextNode();
  let any = false;
  while (node) {
    if (range.intersectsNode(node) && (node.textContent || '').length > 0) {
      const from = node === range.startContainer ? range.startOffset : 0;
      const to = node === range.endContainer ? range.endOffset : (node.textContent || '').length;
      if (to > from) {
        if (!subOf(node, editable)) return false;
        any = true;
      }
    }
    node = node === range.commonAncestorContainer ? null : walker.nextNode();
  }
  return any;
};

/**
 * Put `content` at a collapsed point, outside any subscript: a point inside a
 * <sub> splits it, the content going between the two halves.
 */
const insertOutsideSub = (point: Range, content: Node, editable: HTMLElement): void => {
  const sub = subOf(point.startContainer, editable);
  if (!sub) {
    point.insertNode(content);
    return;
  }
  const tail = document.createRange();
  tail.setStart(point.startContainer, point.startOffset);
  tail.setEnd(sub, sub.childNodes.length);
  const after = tail.extractContents();
  // A fragment empties as it is inserted, so note its last node first.
  const lastInserted = content.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? content.lastChild : content;
  sub.after(content);
  if (lastInserted && (after.textContent || '').length > 0) {
    const rest = sub.cloneNode(false) as HTMLElement;
    rest.appendChild(after);
    (lastInserted as ChildNode).after(rest);
  }
  if (!(sub.textContent || '')) sub.remove();
};

/**
 * Subscript typed next, at a collapsed caret. The browser's own
 * `execCommand('subscript')` cannot be used for this: at a caret inside a
 * <sub> it nests a second <sub> instead of leaving the first, so every
 * character after the first variable came out subscripted. So the toggle is
 * remembered here, and the next typed text is put in (or out of) a <sub> by
 * hand, in a text node of its own that the caret then stays in.
 */
interface PendingSubscript {
  editable: HTMLElement;
  node: Node;
  offset: number;
  on: boolean;
}

let pending: PendingSubscript | null = null;
let listening = false;

const caretIsAt = (at: PendingSubscript): boolean => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return false;
  const range = selection.getRangeAt(0);
  return range.startContainer === at.node && range.startOffset === at.offset;
};

// The field committed a change made by script: neither LiveText nor a
// paragraph hears one otherwise (see notifyLiveTextInput).
const notifyInput = (editable: HTMLElement) =>
  editable.dispatchEvent(new Event('input', { bubbles: true }));

const placeCaret = (node: Node, offset: number) => {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

const handleBeforeInput = (e: InputEvent) => {
  const at = pending;
  if (!at) return;
  if (e.inputType !== 'insertText' || !e.data || !caretIsAt(at)) {
    pending = null;
    return;
  }
  pending = null;
  e.preventDefault();
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const point = selection.getRangeAt(0);
  const text = document.createTextNode(e.data);
  if (at.on) {
    const sub = document.createElement('sub');
    sub.appendChild(text);
    point.insertNode(sub);
  } else {
    insertOutsideSub(point, text, at.editable);
  }
  placeCaret(text, text.length);
  notifyInput(at.editable);
};

const handleSelectionChange = () => {
  if (pending && !caretIsAt(pending)) pending = null;
};

const listen = () => {
  if (listening) return;
  listening = true;
  document.addEventListener('beforeinput', handleBeforeInput as EventListener, true);
  document.addEventListener('selectionchange', handleSelectionChange);
};

/**
 * Toggle subscript on the selection, or -- with a collapsed caret -- for the
 * text typed next, in whichever editable field holds the selection. Returns
 * whether it applied. The DOM gets a `<sub>`; a paragraph keeps it as HTML
 * and a LiveText reads it back as markup.
 */
export const toggleSubscript = (): boolean => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  const editable = editableOf(range.commonAncestorContainer);
  if (!editable) return false;
  listen();

  if (range.collapsed) {
    const on = pending && pending.editable === editable && caretIsAt(pending)
      ? !pending.on
      : !subOf(range.startContainer, editable);
    pending = { editable, node: range.startContainer, offset: range.startOffset, on };
    return true;
  }

  pending = null;
  const on = !isSelectionSubscript();
  const content = range.extractContents();
  unwrapSubs(content);
  const first = content.firstChild;
  const last = content.lastChild;
  if (!first || !last) return true;
  if (on) {
    // Inside a <sub> already (a selection that was partly subscript), the
    // content needs no second one.
    if (subOf(range.startContainer, editable)) {
      range.insertNode(content);
    } else {
      const sub = document.createElement('sub');
      sub.appendChild(content);
      range.insertNode(sub);
    }
  } else {
    insertOutsideSub(range, content, editable);
  }
  const selected = document.createRange();
  selected.setStartBefore(first);
  selected.setEndAfter(last);
  selection.removeAllRanges();
  selection.addRange(selected);
  notifyInput(editable);
  return true;
};
